import { useDateNow, useReduxState } from 'hooks';
import { addMonths } from 'date-fns';
import { htmlToPlainText } from 'shared-helpers/common';
import { getFormattedDate } from 'lib/common';
import { DisplayFormattedDate } from 'components/common';
import { CardProgressStatus } from 'components/common/tasks';
import { AchievementsTileContent } from '.';

import type { AchievementData, AchievementExtended } from 'shared-types';
import { getMessageWithPlaceholders } from 'shared-locales';

interface Props {
  achievement: Pick<
    AchievementExtended,
    'categories' | 'description' | 'title' | 'type'
  >;
  achievementData: Pick<
    AchievementData,
    | 'completionDate'
    | 'expirationDate'
    | 'numberOfCompletedTemplateItems'
    | 'status'
  >;
  numberOfGroupCompletions: number;
  numberOfTemplateItems: number;
}

const AchievementsTile = ({
  achievement,
  achievementData,
  numberOfGroupCompletions,
  numberOfTemplateItems
}: Props) => {
  const { dateNow } = useDateNow();

  const { messages, theme } = useReduxState();

  const expired =
    achievementData.expirationDate &&
    Date.parse(achievementData.expirationDate as string) < dateNow;

  const expiring =
    !expired &&
    achievementData.expirationDate &&
    new Date(Date.parse(achievementData.expirationDate as string)) <
      addMonths(dateNow, 3);

  const hasMultipleCompletions = numberOfGroupCompletions > 1;

  return (
    <div className="achievement">
      <AchievementsTileContent achievement={achievement}>
        {!hasMultipleCompletions && achievementData?.status === 'completed' ? (
          <div className="date">
            <DisplayFormattedDate date={achievementData.completionDate} />
          </div>
        ) : null}

        {achievement.description ? (
          <div className="description">
            {htmlToPlainText(achievement.description)}
          </div>
        ) : null}

        <div className="status">
          {hasMultipleCompletions ? (
            <CardProgressStatus
              icon="copy"
              label={getMessageWithPlaceholders({
                message: messages.achievementNumberOfAchievementCompletions,
                placeholders: { numberOfCompletions: numberOfGroupCompletions }
              })}
            />
          ) : expired ? (
            <CardProgressStatus
              category="danger"
              icon="check"
              label={messages.commonExpired}
              tooltip={`${messages.commonExpired} ${getFormattedDate({
                date: achievementData.expirationDate
              })}`}
            />
          ) : expiring ? (
            <CardProgressStatus
              category="warning"
              icon="check"
              label={messages.commonExpiring}
              tooltip={`${messages.commonExpires} ${getFormattedDate({
                date: achievementData.expirationDate
              })}`}
            />
          ) : achievementData?.status === 'completed' ? (
            <CardProgressStatus
              category="success"
              icon="check"
              label={messages.commonCompleted}
            />
          ) : achievementData &&
            (achievement.type === 'conversation' ||
              achievement.type === 'group-conversation' ||
              achievement.type === 'tasklist') ? (
            <CardProgressStatus
              label={
                achievementData.status === 'inprogress'
                  ? messages.commonInProgress
                  : messages.commonNotStarted
              }
              progress={
                (achievementData.numberOfCompletedTemplateItems || 0) /
                numberOfTemplateItems
              }
              progressShowIfZero={true}
              progressValueLabel={`${
                achievementData.numberOfCompletedTemplateItems || 0
              }/${numberOfTemplateItems}`}
            />
          ) : achievementData && !achievementData.status ? (
            // achievement is from a learning path
            <CardProgressStatus
              category="default"
              icon="minus"
              label={messages.commonNoCompletion}
            />
          ) : null}
        </div>
      </AchievementsTileContent>

      <style jsx>{`
        .achievement {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          box-shadow: ${theme.cardBoxShadow};
          cursor: pointer;
          height: 100%;
          padding: 15px;
          transition: box-shadow 0.3s;
        }
        .achievement:focus,
        .achievement:hover {
          box-shadow: ${theme.cardBoxShadowActive};
        }
        .date {
          color: ${theme.textColorLight};
          font-size: 1.4rem;
          font-weight: 600;
          margin-top: 5px;
        }
        .description {
          color: ${theme.textColorLight};
          display: -webkit-box;
          font-size: 1.4rem;
          margin-top: 5px;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
        .status {
          display: inline-flex;
          margin-top: auto;
          padding-top: 15px;
          padding-bottom: 2px;
        }
      `}</style>
    </div>
  );
};

export default AchievementsTile;
